import { styled } from 'dripsy';
import React, { forwardRef, useEffect } from 'react';
import * as ScreenOrientation from 'expo-screen-orientation';
import * as StatusBar from 'expo-status-bar';
import * as NavigationBar from 'expo-navigation-bar';
import * as SystemUI from 'expo-system-ui';
import { View } from 'react-native';
import {
  useSafeAreaInsets,
  EdgeInsets,
} from 'react-native-safe-area-context';
import { observer } from 'mobx-react-lite';
import { Video } from 'expo-av';

import { IVideoPlayerProps, VideoPlayer } from '../VideoPlayer';
import { IS_ANDROID } from '@utils';

interface IContainerStyleProps {
  insets: EdgeInsets;
}

const Container = styled(View)((props: IContainerStyleProps) => ({
  bg: '$black',
  height: '100%',
  width: '100%',
  pl: props.insets.left && props.insets.right ? props.insets.left : 0,
  pr:
    props.insets.left && props.insets.right ? props.insets.right : 0,
}));

export interface IFullScreenVideoPlayerViewProps
  extends IVideoPlayerProps {
  keyId?: React.Key | null;
}

export const FullScreenVideoPlayerView = observer(
  forwardRef<Video, IFullScreenVideoPlayerViewProps>(
    ({ keyId, ...props }, ref) => {
      const insets = useSafeAreaInsets();

      useEffect(() => {
        const subscription =
          ScreenOrientation.addOrientationChangeListener((e) => {
            if (
              e.orientationInfo.orientation ===
                ScreenOrientation.Orientation.LANDSCAPE_LEFT ||
              e.orientationInfo.orientation ===
                ScreenOrientation.Orientation.LANDSCAPE_RIGHT
            ) {
              StatusBar.setStatusBarHidden(true, 'fade');
              if (IS_ANDROID) {
                NavigationBar.setVisibilityAsync('hidden');
              }
            } else {
              StatusBar.setStatusBarHidden(false, 'fade');
              if (IS_ANDROID) {
                NavigationBar.setVisibilityAsync('visible');
              }
            }
          });

        return () => {
          subscription.remove();
        };
      }, []);

      useEffect(() => {
        if (props.isInFullScreen) {
          SystemUI.setBackgroundColorAsync('black');
          ScreenOrientation.unlockAsync();
          StatusBar.setStatusBarStyle('light');
        } else {
          SystemUI.setBackgroundColorAsync('white');
          ScreenOrientation.lockAsync(
            ScreenOrientation.OrientationLock.PORTRAIT_UP,
          );
          StatusBar.setStatusBarStyle('dark');
        }

        return () => {
          SystemUI.setBackgroundColorAsync('white');
          StatusBar.setStatusBarHidden(false, 'fade');
          StatusBar.setStatusBarStyle('dark');
          if (IS_ANDROID) {
            NavigationBar.setVisibilityAsync('visible');
          }

          ScreenOrientation.lockAsync(
            ScreenOrientation.OrientationLock.PORTRAIT_UP,
          );
        };
      }, [props.isInFullScreen]);

      return (
        <Container insets={insets}>
          {/* By using key we remount the video player when we navigate to
          the next/previous tutorial, due to the video player being
          inside Portal it is not unmounting on changing tutorial
          (screen) that causes persisting state from the last player
          that for us is not suitable, and by remounting we reset
          player's state */}
          <VideoPlayer ref={ref} key={keyId} {...props} />
        </Container>
      );
    },
  ),
);
